import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import {
  LuLayers,
  LuEye,
  LuEyeOff,
  LuLock,
  LuLockOpen,
  LuGripVertical,
  LuX,
  LuMegaphone,
  LuType,
  LuVideo,
  LuImage,
  LuChevronRight,
  LuChevronDown,
} from "react-icons/lu";
import { SceneIconButton } from "../right_panel/RightIconStrip";
import { changeBoxLayer, updateBoxTimeline, selectBox } from "../../../common/actions";
import { getThumbUrl, getLayerName } from "./IdeaTimeline/utils/buildLayers";
import { getVideoThumbnail } from "./IdeaTimeline/utils/videoThumbs";
import { classifyPluginTrackType } from "../../../common/media_track_types.es6";
import { getYoutubeThumbnailUrl } from "../../../common/youtube_tools";
import i18n from "i18next";

// Hover/press states via a scoped <style> tag so rows react without per-row
// React state (which would re-render mid-drag and abort the native HTML5 drag).
// Selected rows set an inline background, which wins over these non-!important
// rules, so the selected highlight survives hover.
const PANEL_CSS = `
.aid-layers-row { transition: background 120ms ease; }
.aid-layers-row:hover { background: #f1f5f9; }
.aid-layers-row:hover .aid-layers-grip { opacity: 1; }
.aid-layers-grip { opacity: 0.45; transition: opacity 120ms ease; }
.aid-layers-iconbtn { transition: background 120ms ease, color 120ms ease; }
.aid-layers-iconbtn:hover:not(:disabled) { background: #f1f5f9; color: #334155; }
.aid-layers-iconbtn:disabled { opacity: 0.4; cursor: default; }
.aid-layers-unit { transition: opacity 120ms ease, background 120ms ease; }
.aid-layers-unit--drag-preview {
  opacity: 0.52;
  background: #eef2ff;
  border-radius: 10px;
  outline: 1px dashed #6366f1;
  outline-offset: -1px;
  box-shadow: 0 3px 10px rgba(79,70,229,0.12);
}
.aid-layers-unit--drag-preview .aid-layers-row { background: transparent !important; }
.aid-layers-group-members {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 3px 0 2px 22px;
  padding-left: 8px;
  border-left: 1px solid #dbe3ef;
}
.aid-layers-group-members .aid-layers-row:hover { background: #f8fafc; }
`;

const GROUP_ICONS = { image: LuImage, video: LuVideo, audio: LuMegaphone, text: LuType };

function PanelThumb({ url, trackType, name, compact = false }) {
  const isYt = !!getYoutubeThumbnailUrl(url);
  const isVideoFile = trackType === "video" && url && !isYt;
  const [videoSrc, setVideoSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setVideoSrc(null);
    if (isVideoFile) {
      getVideoThumbnail(url, 0.1).then((res) => {
        if (!cancelled) setVideoSrc(res);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [url, isVideoFile]);

  const boxStyle = {
    width: compact ? 28 : 34,
    height: compact ? 28 : 34,
    flexShrink: 0,
    borderRadius: 6,
    border: "1px solid #e2e8f0",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    overflow: "hidden",
  };
  const imgSrc = isVideoFile ? videoSrc : isYt ? getYoutubeThumbnailUrl(url) : null;
  if (imgSrc || (trackType === "image" && url)) {
    return (
      <div
        style={{
          ...boxStyle,
          background: `#f1f5f9 url(${imgSrc || url}) center/cover no-repeat`,
        }}
        title={name}
      />
    );
  }
  const Icon = GROUP_ICONS[trackType];
  return (
    <div style={boxStyle} title={name}>
      {Icon ? <Icon size={compact ? 13 : 15} /> : null}
    </div>
  );
}

function LayersPanel({
  sceneId,
  container = 0,
  boxes = [],
  boxesById = {},
  pluginToolbarsById = {},
  boxSelected,
  dispatch,
}) {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  // Ref (not state) for the drag source: calling setState during a drag
  // re-renders the dragged node and aborts the native HTML5 drag.
  const dragRef = useRef(null);
  const dragPreviewIndexRef = useRef(null);
  const dragRowRectsRef = useRef([]);
  const [dragPreview, setDragPreview] = useState(null); // { sourceIndex, destinationIndex }
  const justDroppedRef = useRef(false);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  // Frontmost first for display; `.boxes` is bottom->top, so reverse it.
  const displayIds = [...boxes].reverse().filter((id) => boxesById[id]);

  // Solo boxes and shared timeline rows are reordered as units.
  const trackTypeOf = (id) =>
    classifyPluginTrackType(pluginToolbarsById[id]?.pluginId || boxesById[id]?.pluginId);
  const units = (() => {
    const arr = [];
    const byKey = new Map();
    displayIds.forEach((id) => {
      const trackId = boxesById[id]?.timeline?.trackId;
      const type = trackTypeOf(id);
      const key = trackId && type ? `track:${trackId}:${type}` : null;
      if (!key) {
        arr.push({ kind: "solo", id });
        return;
      }
      let group = byKey.get(key);
      if (!group) {
        group = { kind: "group", key, type, memberIds: [] };
        byKey.set(key, group);
        arr.push(group);
      }
      group.memberIds.push(id);
    });
    return arr.map((u) =>
      u.kind === "group" && u.memberIds.length < 2 ? { kind: "solo", id: u.memberIds[0] } : u
    );
  })();

  const groupTypeLabel = (type) =>
    type === "image"
      ? i18n.t("TimelineRowImages") || "Görseller"
      : type === "video"
        ? i18n.t("TimelineRowVideos") || "Videolar"
        : type === "text"
          ? i18n.t("TimelineRowTexts") || "Metinler"
          : i18n.t("TimelineRowAudios") || "Sesler";

  // Panel açıkken seçili elemanın üstündeki over-box aksiyon barını gizle:
  // kullanıcı bir elemanı seçtikten sonra ikinci bir menü açtıysa artık işini
  // o menüden yapacaktır, bar ise #topPillRoot (z-index 104) ile bu panelin de
  // üstünde durup üzerini kapatıyor. ToolsPanel / kırpma katmanıyla aynı body
  // sınıfı kanalı (_editorBox.scss); ayrı bir sınıf adı, çünkü EditorApp kendi
  // `right-drawer-open` sınıfını her render'da toggle ediyor — paylaşsalardı
  // biri diğerinin yazdığını siler.
  useEffect(() => {
    document.body.classList.toggle("layers-panel-open", open);
    return () => document.body.classList.remove("layers-panel-open");
  }, [open]);

  // Close on Escape or a click anywhere outside the panel/toggle. Capture phase
  // so it fires even though the panel stops mousedown propagation (used to keep
  // canvas clicks from deselecting). The toggle is excluded so its own click
  // handles open/close without this listener fighting it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e) => {
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      const onToggle = toggleRef.current && toggleRef.current.contains(e.target);
      if (!inPanel && !onToggle) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown, true);
    };
  }, [open]);

  const isVisible = (box) => box?.timeline?.visible !== false;
  const isLocked = (box) => box?.timeline?.locked === true;

  const applyUnits = (nextUnits) => {
    const flat = nextUnits.flatMap((u) => (u.kind === "solo" ? [u.id] : u.memberIds));
    dispatch(changeBoxLayer(null, sceneId, container, "reorder", [...flat].reverse()));
    justDroppedRef.current = true;
  };

  const onUnitDrop = (destinationIndex) => {
    const d = dragRef.current;
    dragRef.current = null;
    dragPreviewIndexRef.current = null;
    dragRowRectsRef.current = [];
    setDragPreview(null);
    if (!d || d.type !== "unit" || d.index === destinationIndex) return;
    const next = [...units];
    const [moved] = next.splice(d.index, 1);
    next.splice(destinationIndex, 0, moved);
    applyUnits(next);
  };

  const beginUnitDrag = (index, dragElement) => {
    const drag = { type: "unit", index };
    dragRef.current = drag;
    dragPreviewIndexRef.current = index;
    const list = dragElement?.closest(".aid-layers-list");
    dragRowRectsRef.current = list
      ? Array.from(list.querySelectorAll(":scope > .aid-layers-unit")).map((el) => {
          const rect = el.getBoundingClientRect();
          return { top: rect.top, bottom: rect.bottom };
        })
      : [];
    requestAnimationFrame(() => setDragPreview({ sourceIndex: index, destinationIndex: index }));
    return drag;
  };

  const endUnitDrag = () => {
    dragRef.current = null;
    dragPreviewIndexRef.current = null;
    dragRowRectsRef.current = [];
    setDragPreview(null);
  };

  const setVisibleDom = (id, visible) => {
    // The static editor canvas honors visibility via direct DOM style, mirroring
    // EditorApp's timeline eye handler (there is no React re-render for it).
    const el = document.getElementById(`box-${id}`);
    if (el) {
      el.style.opacity = visible ? "" : "0";
      el.style.pointerEvents = visible ? "" : "none";
    }
  };

  const toggleVisible = (id, box) => {
    const next = !isVisible(box);
    dispatch(updateBoxTimeline(id, { visible: next }));
    setVisibleDom(id, next);
  };

  const toggleLock = (id, box) => {
    dispatch(updateBoxTimeline(id, { locked: !isLocked(box) }));
  };

  const allHidden = displayIds.length > 0 && displayIds.every((id) => !isVisible(boxesById[id]));
  const allLocked = displayIds.length > 0 && displayIds.every((id) => isLocked(boxesById[id]));

  const setAllVisible = (visible) => {
    displayIds.forEach((id) => {
      dispatch(updateBoxTimeline(id, { visible }));
      setVisibleDom(id, visible);
    });
  };

  const setAllLocked = (locked) => {
    displayIds.forEach((id) => dispatch(updateBoxTimeline(id, { locked })));
  };

  const toggleGroupExpanded = (key) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const iconBtnStyle = {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    color: "#64748b",
    flexShrink: 0,
    padding: 0,
  };

  const rowBase = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 6px 6px 4px",
    borderRadius: 8,
    cursor: "pointer",
    userSelect: "none",
    position: "relative",
  };

  const gripStyle = (locked) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    flexShrink: 0,
    color: locked ? "#cbd5e1" : "#94a3b8",
    cursor: locked ? "not-allowed" : "grab",
  });

  const dragOverProps = (unitIndex) => ({
    onDragOver: (e) => {
      const d = dragRef.current;
      if (!d || d.type !== "unit") return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      // Use drag-start geometry to avoid oscillation while rows reorder.
      const rects = dragRowRectsRef.current;
      let boundary = unitIndex;
      if (rects.length === units.length) {
        boundary = rects.findIndex((rect) => e.clientY < (rect.top + rect.bottom) / 2);
        if (boundary === -1) boundary = units.length;
      } else {
        const rect = e.currentTarget.getBoundingClientRect();
        boundary = unitIndex + (e.clientY >= rect.top + rect.height / 2 ? 1 : 0);
      }
      const destinationIndex = Math.max(
        0,
        Math.min(units.length - 1, boundary - (d.index < boundary ? 1 : 0))
      );
      if (dragPreviewIndexRef.current !== destinationIndex) {
        dragPreviewIndexRef.current = destinationIndex;
        setDragPreview({ sourceIndex: d.index, destinationIndex });
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      onUnitDrop(dragPreviewIndexRef.current ?? unitIndex);
    },
  });

  const renderEyeLock = (visible, locked, onEye, onLock) => (
    <>
      <button
        type="button"
        className="aid-layers-iconbtn"
        style={iconBtnStyle}
        onClick={(e) => {
          e.stopPropagation();
          onEye();
        }}
        title={visible ? i18n.t("layersPanel.hide", "Hide") : i18n.t("layersPanel.show", "Show")}
      >
        {visible ? <LuEye size={16} /> : <LuEyeOff size={16} />}
      </button>
      <button
        type="button"
        className="aid-layers-iconbtn"
        style={{ ...iconBtnStyle, color: locked ? "#4f46e5" : "#64748b" }}
        onClick={(e) => {
          e.stopPropagation();
          onLock();
        }}
        title={locked ? i18n.t("layersPanel.unlock", "Unlock") : i18n.t("layersPanel.lock", "Lock")}
      >
        {locked ? <LuLock size={16} /> : <LuLockOpen size={16} />}
      </button>
    </>
  );

  const renderBoxRow = ({ id, key, indent, dragStart, unitIndex }) => {
    const box = boxesById[id];
    const thumb = getThumbUrl(box, pluginToolbarsById);
    const name = getLayerName(box, pluginToolbarsById);
    const selected = id === boxSelected;
    const visible = isVisible(box);
    const locked = isLocked(box);
    return (
      <div
        key={key}
        className="aid-layers-row"
        {...dragOverProps(unitIndex)}
        onClick={() => {
          if (justDroppedRef.current) {
            justDroppedRef.current = false;
            return;
          }
          dispatch(selectBox(id, box));
        }}
        style={{
          ...rowBase,
          ...(indent ? { gap: 7, padding: "4px 6px 4px 2px", borderRadius: 7 } : {}),
          opacity: visible ? 1 : 0.55,
          ...(selected ? { background: "#eef2ff", boxShadow: "inset 3px 0 0 #6366f1" } : {}),
        }}
      >
        {dragStart ? (
          <span
            className="aid-layers-grip"
            draggable={!locked}
            onDragStart={(e) => {
              dragRef.current = dragStart(e.currentTarget);
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", id);
            }}
            onDragEnd={() => {
              endUnitDrag();
            }}
            onClick={(e) => e.stopPropagation()}
            title={
              locked
                ? i18n.t("layersPanel.lockedNoDrag", "Locked")
                : i18n.t("layersPanel.dragToReorder", "Drag to reorder")
            }
            style={gripStyle(locked)}
          >
            <LuGripVertical size={15} />
          </span>
        ) : (
          <span style={{ width: 6, flexShrink: 0 }} />
        )}

        <PanelThumb url={thumb} trackType={trackTypeOf(id)} name={name} compact={indent} />

        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: indent ? 12 : 13,
            fontWeight: selected ? 600 : 450,
            color: "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={name}
        >
          {name}
        </div>

        {renderEyeLock(
          visible,
          locked,
          () => toggleVisible(id, box),
          () => toggleLock(id, box)
        )}
      </div>
    );
  };

  const renderGroupRow = (unit, unitIndex) => {
    const expanded = expandedGroups.has(unit.key);
    const membersVisible = unit.memberIds.every((mid) => isVisible(boxesById[mid]));
    const membersLocked = unit.memberIds.every((mid) => isLocked(boxesById[mid]));
    const hasSelected = unit.memberIds.includes(boxSelected);
    const Icon = GROUP_ICONS[unit.type] || LuLayers;
    return (
      <React.Fragment key={unit.key}>
        <div
          className="aid-layers-row"
          {...dragOverProps(unitIndex)}
          onClick={() => {
            if (justDroppedRef.current) {
              justDroppedRef.current = false;
              return;
            }
            toggleGroupExpanded(unit.key);
          }}
          style={{
            ...rowBase,
            opacity: membersVisible ? 1 : 0.55,
            ...(hasSelected && !expanded
              ? { background: "#eef2ff", boxShadow: "inset 3px 0 0 #6366f1" }
              : {}),
          }}
        >
          <span
            className="aid-layers-grip"
            draggable={!membersLocked}
            onDragStart={(e) => {
              beginUnitDrag(unitIndex, e.currentTarget);
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", unit.key);
            }}
            onDragEnd={() => {
              endUnitDrag();
            }}
            onClick={(e) => e.stopPropagation()}
            title={
              membersLocked
                ? i18n.t("layersPanel.lockedNoDrag", "Locked")
                : i18n.t("layersPanel.dragGroup", "Drag to move the whole group")
            }
            style={gripStyle(membersLocked)}
          >
            <LuGripVertical size={15} />
          </span>

          {/* Type icon box with the member count badge — timeline row identity */}
          <div
            style={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: 6,
              border: "1px solid #e2e8f0",
              background: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              position: "relative",
            }}
          >
            <Icon size={16} />
            <span
              style={{
                position: "absolute",
                right: -5,
                bottom: -5,
                minWidth: 15,
                height: 15,
                padding: "0 3px",
                borderRadius: 8,
                background: "#6366f1",
                color: "#fff",
                fontSize: 9.5,
                fontWeight: 700,
                lineHeight: "15px",
                textAlign: "center",
              }}
            >
              {unit.memberIds.length}
            </span>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              fontWeight: 600,
              color: "#0f172a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {groupTypeLabel(unit.type)}
          </div>

          <span style={{ color: "#94a3b8", display: "flex", flexShrink: 0 }}>
            {expanded ? <LuChevronDown size={15} /> : <LuChevronRight size={15} />}
          </span>

          {renderEyeLock(
            membersVisible,
            membersLocked,
            () =>
              unit.memberIds.forEach((mid) => {
                dispatch(updateBoxTimeline(mid, { visible: !membersVisible }));
                setVisibleDom(mid, !membersVisible);
              }),
            () =>
              unit.memberIds.forEach((mid) =>
                dispatch(updateBoxTimeline(mid, { locked: !membersLocked }))
              )
          )}
        </div>
        {expanded && (
          <div className="aid-layers-group-members">
            {unit.memberIds.map((mid) =>
              renderBoxRow({
                id: mid,
                key: `${unit.key}:${mid}`,
                indent: true,
                dragStart: null,
                unitIndex,
              })
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <span ref={toggleRef} style={{ display: "flex" }}>
        <SceneIconButton
          label={i18n.t("layersPanel.title", "Layers")}
          onClick={() => setOpen((o) => !o)}
          disabled={displayIds.length === 0}
        >
          <LuLayers size={20} />
        </SceneIconButton>
      </span>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={i18n.t("layersPanel.title", "Layers")}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              right: 88,
              bottom: 280,
              width: 268,
              maxHeight: 360,
              display: "flex",
              flexDirection: "column",
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              boxShadow: "0 8px 28px rgba(15,23,42,0.16)",
              zIndex: 103,
              overflow: "hidden",
            }}
          >
            <style>{PANEL_CSS}</style>

            {/* Header — stays put while the list scrolls */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "9px 8px 9px 12px",
                borderBottom: "1px solid #f1f5f9",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
                  {i18n.t("layersPanel.title", "Layers")}
                </span>
                {/* Main number matches the visible rows; groups fold members,
                    so the element total is shown alongside when they differ. */}
                <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}>
                  {units.length}
                </span>
                {units.length !== displayIds.length && (
                  <span style={{ fontSize: 10.5, fontWeight: 500, color: "#b6c2d4" }}>
                    · {displayIds.length} {i18n.t("layersPanel.elements", "element")}
                  </span>
                )}
              </div>

              <button
                type="button"
                className="aid-layers-iconbtn"
                style={iconBtnStyle}
                onClick={() => setAllVisible(allHidden)}
                disabled={displayIds.length === 0}
                aria-label={
                  allHidden
                    ? i18n.t("layersPanel.showAll", "Show all")
                    : i18n.t("layersPanel.hideAll", "Hide all")
                }
                title={
                  allHidden
                    ? i18n.t("layersPanel.showAll", "Show all")
                    : i18n.t("layersPanel.hideAll", "Hide all")
                }
              >
                {allHidden ? <LuEyeOff size={16} /> : <LuEye size={16} />}
              </button>

              <button
                type="button"
                className="aid-layers-iconbtn"
                style={{ ...iconBtnStyle, color: allLocked ? "#4f46e5" : "#64748b" }}
                onClick={() => setAllLocked(!allLocked)}
                disabled={displayIds.length === 0}
                aria-label={
                  allLocked
                    ? i18n.t("layersPanel.unlockAll", "Unlock all")
                    : i18n.t("layersPanel.lockAll", "Lock all")
                }
                title={
                  allLocked
                    ? i18n.t("layersPanel.unlockAll", "Unlock all")
                    : i18n.t("layersPanel.lockAll", "Lock all")
                }
              >
                {allLocked ? <LuLock size={16} /> : <LuLockOpen size={16} />}
              </button>

              <div style={{ width: 1, height: 16, background: "#e2e8f0", margin: "0 2px" }} />

              <button
                type="button"
                className="aid-layers-iconbtn"
                style={iconBtnStyle}
                onClick={() => setOpen(false)}
                aria-label={i18n.t("close", "Close")}
                title={i18n.t("close", "Close")}
              >
                <LuX size={17} />
              </button>
            </div>

            {/* Scrollable layer list — solo rows and collapsed group rows */}
            <div
              className={`aid-layers-list${dragPreview ? " aid-layers-list--dragging" : ""}`}
              onDragOver={(e) => {
                if (e.target !== e.currentTarget || !dragRef.current) return;
                e.preventDefault();
                const rects = dragRowRectsRef.current;
                let boundary = rects.findIndex((rect) => e.clientY < (rect.top + rect.bottom) / 2);
                if (boundary === -1) boundary = units.length;
                const sourceIndex = dragRef.current.index;
                const destinationIndex = Math.max(
                  0,
                  Math.min(units.length - 1, boundary - (sourceIndex < boundary ? 1 : 0))
                );
                if (dragPreviewIndexRef.current === destinationIndex) return;
                dragPreviewIndexRef.current = destinationIndex;
                setDragPreview({
                  sourceIndex,
                  destinationIndex,
                });
              }}
              onDrop={(e) => {
                if (!dragRef.current) return;
                e.preventDefault();
                onUnitDrop(dragPreviewIndexRef.current ?? dragRef.current.index);
              }}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: 6,
                gap: 3,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {units.map((unit, unitIndex) => {
                const previewOrder = dragPreview
                  ? unitIndex === dragPreview.sourceIndex
                    ? dragPreview.destinationIndex
                    : unitIndex < dragPreview.sourceIndex &&
                        unitIndex >= dragPreview.destinationIndex
                      ? unitIndex + 1
                      : unitIndex > dragPreview.sourceIndex &&
                          unitIndex <= dragPreview.destinationIndex
                        ? unitIndex - 1
                        : unitIndex
                  : unitIndex;
                const isDragPreview = dragPreview?.sourceIndex === unitIndex;
                return (
                  <div
                    key={unit.kind === "group" ? unit.key : unit.id}
                    className={`aid-layers-unit${isDragPreview ? " aid-layers-unit--drag-preview" : ""}`}
                    style={{ order: previewOrder }}
                  >
                    {unit.kind === "group"
                      ? renderGroupRow(unit, unitIndex)
                      : renderBoxRow({
                          id: unit.id,
                          key: unit.id,
                          indent: false,
                          dragStart: (dragElement) => beginUnitDrag(unitIndex, dragElement),
                          unitIndex,
                        })}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

const mapStateToProps = (state) => ({
  boxesById: state.undoGroup.present.boxesById,
  pluginToolbarsById: state.undoGroup.present.pluginToolbarsById,
  boxSelected: state.undoGroup.present.boxSelected,
});

export default connect(mapStateToProps)(LayersPanel);
